/**
 * Everything hero training for **two** says, and every number it decides with (CAM-02 + CAM-07,
 * GDS §13 "Cooperative verification", MIN §6/§8).
 *
 * Same discipline as `calibration.ts` and `camera-lanes.ts` (D-010/D-023). Three groups:
 *
 *  - {@link COOPERATIVE_VERIFICATION_LABELS} is **verbatim from GDS §13's three cooperative
 *    bullets**; `tests/architecture/cooperative-calibration-copy.test.ts` re-reads the design spec
 *    and fails if a word changes. They are the document's words, for a grown-up — the children read
 *    the prompts below.
 *  - {@link INVENTED_COOPERATIVE_CALIBRATION_COPY} is every word a family actually reads. §13
 *    contains no prompts, no encouragement and no buttons for the cooperative step, so **all of it
 *    is invented and awaiting Jon's copy review** (the D-049(5)/D-061(4) pattern).
 *  - {@link COOPERATIVE_CALIBRATION_TUNABLES} are the numbers. **All invented.** None of them has
 *    met a child, and every one of them fails safe towards "keep waiting warmly" rather than
 *    towards declaring anything about anybody.
 *
 * **The copy rule that is specific to this file.** Two children are standing side by side while it
 * is on screen. Anything that separates them — who went first, who was quicker, who the camera
 * liked better — turns hero training into a comparison, which the non-competitive invariant forbids
 * (ARCH §12, COOP-07) and which recon §B.4/D-076(6) extend to input quality. So the celebration and
 * every status line here are written for a *team*, and the audit test enforces it.
 */

/**
 * Which half of the picture one hero's training is measuring (D-079(4)).
 *
 * Absent for a single child, who has the whole frame and whose training is byte-for-byte what it
 * shipped as. Present for a cooperative sitting, where Hero 1 stands in the left half and Hero 2 in
 * the right (MIN §6, GDS §7) — and where it changes exactly three things and nothing else: where
 * "stand in the ring" means, where the child's three portals are drawn if they skip the sideways
 * step, and that a child who overshoots does not end up with portals in their sibling's half.
 *
 * `from`/`to` are in **mirrored** x, the same space `laneBounds` is expressed in (CAM-04), so there
 * is one convention and not two. It lives in the config layer, beside the halves themselves, so
 * that nothing in `tests/` has to reach across the pose boundary to read a number.
 */
export interface CalibrationLane {
  readonly side: 'left' | 'right';
  readonly from: number;
  readonly to: number;
}

/** GDS §13's three cooperative-verification bullets, verbatim. */
export const COOPERATIVE_VERIFICATION_LABELS = {
  'lane-separation': 'Verify lane separation',
  'both-visible': 'Confirm both players visible',
  'shared-celebration': 'Perform a simple shared celebration',
} as const;

/**
 * Which half of the picture each hero trains in, in **mirrored** x (CAM-04, D-079(4)).
 *
 * Geometry rather than preference: MIN §6 and GDS §7 fix Hero 1 to the left lane and Hero 2 to the
 * right and say they do not swap. Splitting the frame down the middle is what makes each child's
 * three portals *theirs* — a cooperative sitting on whole-frame bounds would read one child as
 * permanently standing in the right-hand portal and the other permanently in the left, for the
 * whole mission, without anything looking broken.
 */
export const COOPERATIVE_LANE_SPANS: Readonly<Record<'left' | 'right', CalibrationLane>> = {
  left: { side: 'left', from: 0, to: 0.5 },
  right: { side: 'right', from: 0.5, to: 1 },
};

/**
 * Copy no document contains. **Every string here is invented and awaiting approval.**
 *
 * Written as hero training for a team, in the second person, naming no mistake and no child: a
 * camera that cannot see somebody is a fact about the room, and two children standing too close
 * together are invited apart rather than told off.
 */
export const INVENTED_COOPERATIVE_CALIBRATION_COPY = {
  /** Screen title. */
  pageTitle: 'Hero training for two',
  /** One line under the title. */
  pageLede: 'One hero at a time, and then a big cheer together.',
  /** The entry point on the readiness screen. */
  start: 'Start hero training for two',
  /** Leaves training early. */
  stop: 'Stop training',
  /** Starts the whole sitting again, from the top. */
  restart: 'Start the training again',
  /**
   * Which hero the trainer is measuring now. Two plain strings rather than a template with a
   * number in it.
   *
   * **Turn-free by ruling (E-R1, Slice E″).** These used to read "it is your turn to show your
   * moves". Hero training genuinely is sequential — the trainer measures one body at a time
   * (PS4-3, D-076(3)) — but the asset brief §F names "your turn" among the phrases this product
   * does not use, and there is no version of that sentence a five-year-old needs. The guidance is
   * unchanged: each line still names the hero, names their side, and asks for the same thing. What
   * is gone is the framing that somebody is waiting for a go.
   */
  heroOneTraining: 'Hero 1, stand on the left side. Show us your moves!',
  heroTwoTraining: 'Hero 2, stand on the right side. Show us your moves!',
  /**
   * What the child who is not training does (PS4-3, D-076(3)).
   *
   * They are invited to the edge of their side and are **not** sent out of the picture: a
   * five-year-old cannot be reliably banished from a room, and a setup that depended on it would
   * break in every family. The trainer watches one side at a time and simply tolerates the other.
   */
  waitingHero:
    'The other hero can wait at the edge of their own side — we watch one hero at a time.',
  /** The heading over the both-heroes-together part. */
  verificationTitle: 'Now both heroes together',
  /** What each of the three together-steps asks the family to do. */
  prompts: {
    'lane-separation': 'Both heroes, stand on your own side with a big space in the middle.',
    'both-visible': 'Stay just where you are, so we can see both heroes.',
    'shared-celebration': 'Hands up together — one big hero cheer!',
  },
  /** Live feedback while a together-step is running. */
  feedback: {
    WAITING_FOR_BOTH: 'We need both heroes in the picture.',
    MOVE_APART: 'Take a big step apart, one hero on each side.',
    HARD_TO_SEE: 'Somewhere a bit brighter, so we can see both heroes.',
    HOLD_STILL: 'Lovely — hold it there.',
    CELEBRATE_TOGETHER: 'Hands up together!',
    PASSED: 'Got it.',
  },
  /** The done-for-now screen. */
  completeTitle: 'Hero team training complete',
  completeBody: 'The suit knows both heroes now.',
  /** Spoken once, when the two heroes cheer together. Team-framed, and about nobody in particular. */
  celebrationSpoken: 'Hero team, together! The suit knows both of you now.',
  /** Leaves the done-for-now screen. */
  done: 'Back',
  /**
   * Why this screen will not train anybody (D-076(4)).
   *
   * Cooperative camera play is Full-Motion only for the MVP, and a two-hero sitting is chosen on
   * the grown-up screen. Said plainly rather than by rendering nothing.
   */
  cooperativeOnly:
    'This training is for two heroes. A grown-up can choose two heroes on the set-up screen, then come back here.',
  /**
   * What two trained heroes get next (Slice D′, replacing C′'s honest build-state line).
   *
   * `missionStillOnKeys` — "Both heroes play the mission with the keyboard for now" — was true for
   * exactly as long as the cooperative camera mission did not exist. It does now, so the sentence
   * is gone rather than reworded around, and what stands in its place is the CAM-09 fact that
   * remains true in every camera sitting: the keys never stop working.
   */
  missionWithCamera:
    'Both heroes can now play the mission with the camera. The keyboard keeps working too, for either hero, at any time.',
} as const;

/**
 * The numbers the together-steps decide with. **All invented** (D-010/D-023 family).
 *
 * The one worth arguing about is {@link celebrationGraceMs}: GDS §13 says the shared celebration
 * must "not require simultaneous academic input", and two five-year-olds getting their hands up at
 * the same instant is not a thing that happens. So the celebration uses a window, in the same
 * spirit as the cooperative participation grace (D-047(2)) — and a longer one, because coordinating
 * with a sibling is slower than pressing a key.
 */
export interface CooperativeCalibrationTunables {
  readonly verifyHoldMs: number;
  readonly verifyMinSamples: number;
  readonly laneSeparationMargin: number;
  readonly celebrationGraceMs: number;
}

export const COOPERATIVE_CALIBRATION_TUNABLES: CooperativeCalibrationTunables = {
  /** Continuous qualifying time before a together-step is satisfied. */
  verifyHoldMs: 800,
  /** Minimum qualifying frames a together-step needs, however fast frames arrive. */
  verifyMinSamples: 3,
  /**
   * How far inside their own half each hero's body must stand before the picture counts as two
   * separated heroes.
   *
   * Deliberately *added to* the resolver's own separation rules rather than replacing them: the
   * resolver decides whether it can tell the two children apart at all, and this decides whether
   * the room is set up the way the mission will need it. A family who pass this check are standing
   * where a mission's portals will be.
   */
  laneSeparationMargin: 0.06,
  /** How far apart in time the two heroes' cheers may be and still be one cheer. */
  celebrationGraceMs: 1_500,
};
